import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../lib/date/formatDate';
import { DATE_FORMAT } from '../../../../lib/resolveFields';
import type { GeneratedKey } from '../../instanceTypes';

interface GeneratedKeyBannerProps {
  generatedKey: GeneratedKey;
  onDismiss: () => void;
}

export function GeneratedKeyBanner({ generatedKey, onDismiss }: GeneratedKeyBannerProps) {
  const { t } = useTranslation('instances');
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(generatedKey.key).then(() => setCopied(true));
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: theme.borderRadius.sm,
        background: `linear-gradient(180deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.03)} 100%)`,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningAmberIcon aria-hidden="true" sx={{ fontSize: theme.typography.body1.fontSize, color: theme.palette.warning.dark }} />
        <Typography
          variant="body2"
          sx={{ color: theme.palette.warning.dark, fontWeight: theme.typography.fontWeightSemiBold, flex: 1, fontSize: theme.typography.fontSizeDescription }}
        >
          {t('generateKey.warning')}
        </Typography>
        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label={t('generateKey.dismiss')}
          sx={{
            color: theme.palette.warning.dark,
            background: alpha(theme.palette.warning.dark, 0.10),
            borderRadius: theme.borderRadius.xs,
            width: theme.iconSize.md,
            height: theme.iconSize.md,
            flexShrink: 0,
            '&:hover': { background: alpha(theme.palette.warning.dark, 0.18) },
          }}
        >
          <DoneIcon sx={{ fontSize: theme.typography.caption.fontSize }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'common.white',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.borderRadius.sm,
          p: 1,
        }}
      >
        <Typography
          component="code"
          sx={{
            flex: 1,
            fontSize: theme.typography.caption.fontSize,
            color: theme.palette.text.body,
            wordBreak: 'break-all',
          }}
        >
          {generatedKey.key}
        </Typography>
        <Tooltip title={copied ? t('generateKey.copied') : t('generateKey.copy')}>
          <Box
            component="button"
            onClick={handleCopy}
            aria-label={copied ? t('generateKey.copied') : t('generateKey.copy')}
            sx={{
              appearance: 'none',
              border: 0,
              cursor: 'pointer',
              bgcolor: theme.palette.primary.main,
              color: 'common.white',
              height: 30,
              px: 2,
              borderRadius: theme.borderRadius.xs,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              fontWeight: theme.typography.fontWeightSemiBold,
              fontSize: theme.typography.caption.fontSize,
              flexShrink: 0,
              '&:hover': { bgcolor: theme.palette.primary.dark },
            }}
          >
            {copied ? (
              <DoneIcon aria-hidden="true" sx={{ fontSize: theme.typography.caption.fontSize }} />
            ) : (
              <ContentCopyIcon aria-hidden="true" sx={{ fontSize: theme.typography.caption.fontSize }} />
            )}
            {copied ? t('generateKey.copied') : t('generateKey.copy')}
          </Box>
        </Tooltip>
      </Box>

      <Typography
        component="small"
        sx={{
          display: 'block',
          mt: 1,
          fontSize: '0.6875rem',
          color: theme.palette.warning.dark,
          opacity: 0.8,
        }}
      >
        {t('generateKey.expiresAt', { date: formatDate(generatedKey.expiresAt, DATE_FORMAT) })}
      </Typography>
    </Box>
  );
}
